/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * `nguonGiuQuyen` ĐI BẰNG KHUNG SỐNG — kho đọc riêng, KHÔNG đi qua CSDL.
 *
 * 🚨 VÌ SAO PHẢI CÓ TỆP NÀY — và đây là một LỖ THẬT, dev_device đoán đúng chỗ, sai cơ chế:
 *
 *   Lượt trước tôi cho máy chủ đẩy `nguonGiuQuyen` trong khung `cabinet.state`, rồi cho giao
 *   diện đọc `output.nguonGiuQuyen` **từ đường REST**. Đo lại thì hai đầu ấy KHÔNG NỐI VỚI
 *   NHAU:
 *
 *     máy chủ  →  ws `cabinet.state` { outputs: [{ …, nguonGiuQuyen }] }
 *     web      →  `useRealtimeSync()` chỉ gọi `invalidateQueries(...)` rồi **VỨT cả khung**
 *     web      →  `GET /api/cabinets/:id`  ← dựng từ CSDL, mà CSDL **KHÔNG CÓ CỘT NÀY**
 *
 *   ⇒ `output.nguonGiuQuyen` **LUÔN LUÔN `undefined`** trên trang. Nhãn tôi vừa dựng không
 *     bao giờ gọi được tên một nguồn nào. Nó không sai — nó **câm**.
 *
 * 📌 Và phép canh của tôi xanh suốt: *"máy chủ THẬT SỰ đẩy `nguonGiuQuyen`"* — đúng, máy chủ
 *    có đẩy. **Trình duyệt đổ nó xuống sàn.** Đúng họ "đo cái cổng, không đo cái dây", lần
 *    thứ SÁU, và lần này cái cổng tôi đo nằm ở ĐẦU BÊN KIA của cái dây bị đứt.
 *    ⇒ Phép mới phải hỏi ở ĐẦU NHẬN, không hỏi ở đầu phát.
 *
 * ⛔ VÌ SAO KHÔNG THÊM CỘT CSDL:
 *   1. `P-2` đang mở; một lượt chuyển lược đồ là một lần nạp thứ hai trong phiên.
 *   2. Sâu hơn: đây là một **sự thật SỐNG**, không phải một sự thật ĐƯỢC LƯU. Ghi nó vào
 *      CSDL là dựng một chỗ để nó **sống sót sau khi tủ mất kết nối** — lúc ấy trang sẽ gọi
 *      tên một nguồn mà không ai biết còn giữ hay không. Một cái tên cũ tệ hơn "chưa biết".
 *
 * ⛔ BA LUẬT CỦA KHO NÀY, cả ba đều nghiêng về phía "chưa biết":
 *   · Mỗi khung THAY nguyên bản đồ của tủ ấy — không vá từng ô, không giữ ô cũ.
 *   · Trường VẮNG trong khung (tủ đời cũ, đường v1) ⇒ **không ghi gì**, KHÔNG ghi `0`.
 *     Ghi `0` là biến *"tủ không nói"* thành *"tủ nói không ai giữ"*.
 *   · Tủ MẤT KẾT NỐI ⇒ **xoá sạch** bản đồ của tủ ấy ⇒ về `'chua-biet'`.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */

/** cabinetId → (chỉ số ngõ 1..3 → mã nguồn giữ, ĐÚNG NHƯ TỦ KHAI). */
type BanDo = Map<string, Map<number, number>>;

const banDo: BanDo = new Map();
const nghe = new Set<() => void>();

/*
 * ⚠ `useSyncExternalStore` so ảnh chụp bằng `Object.is`. Trả một Map mới mỗi lần gọi
 *   `getSnapshot` là vòng lặp vô hạn. ⇒ Ảnh chụp là một SỐ ĐẾM PHIÊN BẢN, và thành phần đọc
 *   giá trị thật qua `docNguonGiuSong()` sau khi số ấy đổi.
 */
let phienBan = 0;

function baoDoi() {
  phienBan++;
  for (const f of nghe) f();
}

export function ngheNguonGiuSong(cb: () => void): () => void {
  nghe.add(cb);
  return () => { nghe.delete(cb); };
}

export const phienBanNguonGiuSong = (): number => phienBan;

/**
 * Nhận MỘT khung `cabinet.state`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * 🚨 `khungNoiVeNguonGiu` KHÔNG PHẢI MỘT CỜ PHÒNG XA — nó bịt một lỗ tôi tự đẻ ra lượt 33.
 *
 * `cabinet.state` được phát từ **HAI** đường, và chỉ một đường biết gì về nguồn giữ:
 *
 *     onKappState (v2)  →  outputs[] CÓ `nguonGiuQuyen`      ⇒ khung NÓI về nguồn giữ
 *     onState     (v1)  →  outputs[] chỉ {index,isOn,enabled} ⇒ khung KHÔNG nói gì cả
 *
 * Tủ v2 **vẫn phát khung v1 mỗi ~2 giây**. Bản đầu của tôi THAY nguyên bản đồ theo mọi khung
 * ⇒ mỗi vòng v1 **xoá sạch tên nguồn** mà khung v2 vừa mang về ⇒ nhãn nhấp nháy
 * *"MÀN HÌNH TỦ"* → *"chưa rõ nguồn"* → *"MÀN HÌNH TỦ"*. Ô (d) chấm trên một cái nhãn nhấp
 * nháy thì chấm gì cũng sai.
 *
 * ⛔ VÀ KHÔNG SUY TỪ SỰ VẮNG MẶT: *"không ngõ nào mang trường ⇒ khung v1"* là SAI, vì sau
 *   `NV108` một tủ mà **cả ba ngõ không ai giữ** cũng cho ra khung y hệt — `GIU_KHONG_AI = 0`
 *   rơi khỏi dây (mặc định proto3). Hai ca ngược nhau, một hình dạng.
 *   ⇒ Người PHÁT phải nói ra mình có ý kiến hay không. Đây là cùng luật đã dùng cho
 *     `'chua-biet'` vs `'khong-ai'`, nâng lên một tầng: **KHUNG KHÔNG NÓI ≠ KHUNG NÓI KHÔNG AI.**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
export function nhanKhungNguonGiu(
  cabinetId: string,
  outputs: Array<{ index: number; nguonGiuQuyen?: number }> | undefined,
  khungNoiVeNguonGiu: boolean,
): void {
  if (!khungNoiVeNguonGiu) return;
  if (!cabinetId || !outputs) return;
  const m = new Map<number, number>();
  for (const o of outputs) {
    /* ⛔ `undefined`/`null` KHÔNG được ghi thành 0 — xem luật 2 ở đầu tệp. */
    if (o.nguonGiuQuyen === undefined || o.nguonGiuQuyen === null) continue;
    if (!Number.isInteger(o.index)) continue;
    m.set(o.index, o.nguonGiuQuyen as number);
  }
  banDo.set(cabinetId, m);
  baoDoi();
}

/** Tủ mất kết nối ⇒ xoá sạch. Một cái tên cũ tệ hơn "chưa biết". */
export function xoaNguonGiuSong(cabinetId: string): void {
  if (!cabinetId) return;
  if (!banDo.has(cabinetId)) return;
  banDo.delete(cabinetId);
  baoDoi();
}

/** `undefined` = CHƯA nghe tủ nói về ngõ này ⇒ `docNguonGiu()` sẽ ra `'chua-biet'`. */
export function docNguonGiuSong(cabinetId: string, index: number): number | undefined {
  return banDo.get(cabinetId)?.get(index);
}

/** Chỉ dùng cho phép đo — không thành phần nào gọi. */
export function xoaHetNguonGiuSong(): void {
  banDo.clear();
  baoDoi();
}
